import React, { Fragment, useMemo } from 'react';
import _ from 'lodash';

import AnomalyDescription from '../report-items/anomalyDescription';
import AnomalyRemarks from '../report-items/anomalyRemarks';
import Conditional from '../../../common/conditional';
import CustomSection from '../../custom-general/custom-section';
import PDFTablePage from '../../../themed/pdf/pdfTablePage';

import { getReportDetails } from '../../../../store/reportDetails';
import { useAppSelector } from '../../../../store/configureStore';
import { getAnomalyClassStyle,  getAnomalyId,  getChunkedData, getIsAnomalyType, matchPhrases, summarize } from '../../../../utils/utilities';
import { Column, CoverDataItem, FVIEquipmentItem, ItemWithAnomaly, Project } from '../../../../utils/models';
import { ExportParams } from '../../../blocks/pdf-export';
import { DAMAGE_CLASS } from '../../../../utils/constants';

import classNames from './style.module.scss';
import config from '../../../../utils/config';

interface AnomalyRecommendationProps {
    projectDetails: Project;
    items: ItemWithAnomaly[];
    exportParams: ExportParams;
    title: string;
    showSummarySection?: boolean;
}

interface RecommendationItem {
    itemId: string;
    anomalyIndex?: number;
    location: string;
    lineId: number;
    id: number;
    description: CoverDataItem[];
    recommendation: CoverDataItem[];
    class: number;
    inspectionDescription?: string;
}

const GeneralAnomalyRecommendation: React.FC<AnomalyRecommendationProps> = ({ exportParams, projectDetails, items, showSummarySection = true, title = 'Anomaly & Recommendation' }) => {
    const { itemCountPerPage } = useAppSelector(getReportDetails);

    const columns: Column[] = useMemo(() => {
        let columns: Column[] = [];

        if (projectDetails.inspectionTypeId === config.supportedTemplates.exxonMobilFVIPiping) {
            columns.push({
                path: 'lineNumber',
                label: 'Line Number'
            });
        }
        
        if (projectDetails.inspectionTypeId === config.supportedTemplates.exxonMobilFVIEquipment) {
            const equipmentColumns = [
                {
                    path: 'equipmentNumber',
                    label: 'Equipment Tag'
                },
                {
                    path: 'equipmentDescription',
                    label: 'Equipment Description'
                },
                {
                    path: 'pIDNumber',
                    label: 'P & ID',
                    content: (item: FVIEquipmentItem) => <span>{matchPhrases(item.hasPID, 'yes') ? item.pIDNumber : 'N/A'}</span>
                },
            ];

            columns = columns.concat(equipmentColumns);
        }
        
        if (projectDetails.inspectionTypeId === config.supportedTemplates.exxonMobilFVIStructure) {
            columns.push({
                path: 'structureMemberName',
                label: 'Structure Member Name'
            });
        }

        return columns;
    }, [projectDetails.inspectionTypeId]);
    
    const mappedItems: RecommendationItem[] = useMemo(() => {
        if (columns.length > 0) {
            return items.flatMap((item) => {
                const combined = columns.reduce((a, b) => _.set(a, b.path, _.get(item, b.path)), {});

                if (item.anomalyItems.length > 0) {
                    return item.anomalyItems.map((anomaly, anomalyIndex) => {
                        const data = {
                            itemId: getAnomalyId(item.id, anomalyIndex + 1),
                            anomalyIndex: anomalyIndex + 1,
                            location: anomaly.location,
                            lineId: item.id, 
                            id: anomaly.id,
                            description: anomaly.description,
                            recommendation: anomaly.recommendation,
                            class: _.get(anomaly, 'class') ?? DAMAGE_CLASS.ZERO.ID,
                            inspectionDescription: item.inspectionDescription,
                        };

                        return _.merge(data, combined) as RecommendationItem;
                    });
                }

                const data = {
                    itemId: item.id.toString(), 
                    location: '',
                    lineId: item.id, 
                    anomalyIndex: undefined,
                    id: 0,
                    description: [],
                    recommendation: [],
                    class: DAMAGE_CLASS.ZERO.ID,
                    inspectionDescription: item.inspectionDescription,
                };
    
                return _.merge(data, combined) as RecommendationItem;
            });
        }

        return [];
    }, [columns, items]);

    const anomalyItemColumns = useMemo(() => {
        return [
            {
                path: 'itemId',
                label: 'Sr. No',
            },
            ...columns,
            {
                path: 'class',
                label: 'Damage Class',
                style: (item: RecommendationItem) => getAnomalyClassStyle({ class: item.class ?? 0 })
            },
            {
                path: 'description',
                label: 'Anomaly Details',
                content: (item: RecommendationItem) => {
                    return (
                        <>
                            {item.description.length > 0 ? <AnomalyDescription lineNumberID={item.lineId} anomalyIndex={item.anomalyIndex} anomaly={item} /> : item.inspectionDescription}
                        </>
                    )
                },
                style: () => ({ textAlign: 'left' })
            },
            {
                path: 'location',
                label: 'Location',
                content: (item: RecommendationItem) => <span>{item.location || 'N/A'}</span>
            },
            {
                path: 'recommendation',
                label: 'Recommendation',
                content: (item: RecommendationItem) => (
                    <>
                        {item.recommendation.length > 0 ? <AnomalyRemarks lineNumberID={item.lineId} anomalyIndex={item.anomalyIndex} anomaly={item} /> : 'N/A'}
                    </>
                )
            },
        ];
    }, [columns]);

    const isAnomalyType = useMemo(() => {
        return projectDetails && getIsAnomalyType(projectDetails);
    }, [projectDetails]);
    
    if (isAnomalyType) {
        return null;
    }

    return ( 
        <Conditional isVisible={projectDetails.inspectionTypeId !== config.supportedTemplates.pcnlStructuralIntegrityReport}>
            {getChunkedData(mappedItems, itemCountPerPage).map((chunk, index) => (
                <Fragment key={index}>
                    <CustomSection pageBreakClass={exportParams.pdfBreakClass} sectionTitle={title}>
                        <div className={classNames.anomalyRecommendation}>
                            <Conditional isVisible={index === 0 && showSummarySection}>
                                <div className="mt-1">
                                    <div className={classNames.anomalyRecommendationDetails}>
                                        <div className={classNames.anomalyRecommendationDetailsItem}>
                                            <div className={classNames.anomalyRecommendationDetailsItemHeader}>Tag</div>  
                                            <div className={classNames.anomalyRecommendationDetailsItemValue}>{summarize(projectDetails.equipmentTag, 20)}</div>  
                                        </div>
                                        <div className={classNames.anomalyRecommendationDetailsItem}>
                                            <div className={classNames.anomalyRecommendationDetailsItemHeader}>Location</div>  
                                            <div className={classNames.anomalyRecommendationDetailsItemValue}>{projectDetails.location}</div>  
                                        </div>
                                        <div className={classNames.anomalyRecommendationDetailsItem}>
                                            <div className={classNames.anomalyRecommendationDetailsItemHeader}>Module</div>  
                                            <div className={classNames.anomalyRecommendationDetailsItemValue}>{projectDetails.module}</div>  
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-1">
                                    <div className={classNames.anomalyRecommendationDetails}>
                                        <div className={classNames.anomalyRecommendationDetailsItem}>
                                            <div className={classNames.anomalyRecommendationDetailsItemHeader}>Inspection Type</div>  
                                            <div className={classNames.anomalyRecommendationDetailsItemValue}>{projectDetails.inspectionTypeName}</div>  
                                        </div>
                                    </div>
                                </div>

                                <div className="my-1"></div>
                            </Conditional>

                            <PDFTablePage data={chunk} columns={anomalyItemColumns as Column[]} />
                        </div>
                    </CustomSection>
                </Fragment>
            ))}
        </Conditional>
    );
};

export default GeneralAnomalyRecommendation;